// @flow
import * as React from 'react';
import {
    getTriggerId,
    isCompleteAccountModalOpen,
    isLoginModalOpen,
    isTracking,
    openCompleteAccountModalFromChecker,
    openConfirmPasswordModalFromChecker,
    openLoginModalFromChecker,
    openSetPasswordModalFromChecker,
    openSignUpModalFromChecker,
    stopTracker,
} from '../../redux/modules/shouldLogin';
import {
    isConfirmPasswordTriggered,
    isProfileComplete,
    isUserAtLeastPartiallyRegistered,
    isUserLoggedIn,
    isUserPartiallyRegistered,
} from '../../redux/modules/user';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import isEqual from 'react-fast-compare';
import uuid from 'uuid';

const mapStateToProps = state => {
    const userLoggedIn = isUserLoggedIn(state);
    const isCompleteProfile = isProfileComplete(state);
    const triggerId = getTriggerId(state);
    const tracking = isTracking(state);
    const loginModalOpen = isLoginModalOpen(state);
    const completeAccountModalOpen = isCompleteAccountModalOpen(state);
    const confirmPasswordTriggered = isConfirmPasswordTriggered(state);
    const userAtLeastPartiallyRegistered = isUserAtLeastPartiallyRegistered(state);
    const userIsPartiallyRegistered = isUserPartiallyRegistered(state);

    return {
        completeAccountModalOpen,
        confirmPasswordTriggered,
        isCompleteProfile,
        loginModalOpen,
        tracking,
        triggerId,
        userAtLeastPartiallyRegistered,
        userIsPartiallyRegistered,
        userLoggedIn,
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            openCompleteAccountModalFromChecker,
            openConfirmPasswordModalFromChecker,
            openLoginModalFromChecker,
            openSetPasswordModalFromChecker,
            openSignUpModalFromChecker,
            stopTracker,
        },
        dispatch
    );

type Props = {
    children: React.Node,
    completeAccountModalOpen: boolean,
    confirmPasswordTriggered: boolean,
    forceConfirmPassword: boolean,
    forceSignUp: boolean,
    isCompleteProfile: boolean,
    loginModalOpen: boolean,
    openCompleteAccountModalFromChecker: Function,
    openConfirmPasswordModalFromChecker: Function,
    openLoginModalFromChecker: Function,
    openSetPasswordModalFromChecker: Function,
    openSignUpModalFromChecker: Function,
    requireCompleteProfile: boolean,
    requireLogin: boolean,
    requireUserAtLeastPartiallyRegistered: boolean,
    runFunction: Function,
    runFunctionArgument: any[] | number | string | any,
    stopTracker: Function,
    tracking: boolean,
    triggerId: string,
    userAtLeastPartiallyRegistered: boolean,
    userIsPartiallyRegistered: boolean,
    userLoggedIn: boolean,
};

type State = {
    guid: string,
};

class ShouldLogInContainer extends React.Component<Props, State> {
    static defaultProps = {
        forceSignUp: true,
        loginModalOpen: false,
        requireCompleteProfile: false,
        requireLogin: true,
        tracking: false,
    };

    state = { guid: uuid.v4() };

    shouldComponentUpdate = (nextProps, nextState) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    componentDidUpdate(prevProps) {
        const {
            completeAccountModalOpen,
            forceConfirmPassword,
            isCompleteProfile,
            loginModalOpen,
            requireLogin,
            requireCompleteProfile,
            runFunction,
            runFunctionArgument,
            tracking,
            userLoggedIn,
            triggerId,
        } = this.props;

        if (this.state.guid === triggerId && tracking) {
            if (forceConfirmPassword) {
                if (
                    prevProps.confirmPasswordTriggered &&
                    prevProps.confirmPasswordTriggered !== this.props.confirmPasswordTriggered
                ) {
                    this.props.stopTracker();
                    runFunction(runFunctionArgument);
                }
            } else {
                if (
                    !loginModalOpen &&
                    !completeAccountModalOpen &&
                    (userLoggedIn || !requireLogin) &&
                    (isCompleteProfile || !requireCompleteProfile)
                ) {
                    this.props.stopTracker();
                    runFunction(runFunctionArgument);
                }

                if (
                    userLoggedIn &&
                    !loginModalOpen &&
                    requireCompleteProfile &&
                    !isCompleteProfile &&
                    !completeAccountModalOpen
                ) {
                    this.props.openCompleteAccountModalFromChecker(triggerId);
                }
            }
        }
    }

    determineModalFlow = userIsPartiallyRegistered => {
        if (userIsPartiallyRegistered) {
            this.props.openSetPasswordModalFromChecker(this.state.guid);
        } else {
            if (this.props.forceSignUp) {
                this.props.openSignUpModalFromChecker(this.state.guid);
            } else {
                this.props.openLoginModalFromChecker(this.state.guid);
            }
        }
    };

    handleClick = () => {
        const {
            forceConfirmPassword,
            forceSignUp,
            userLoggedIn,
            requireLogin,
            isCompleteProfile,
            requireCompleteProfile,
            requireUserAtLeastPartiallyRegistered,
            runFunction,
            runFunctionArgument,
            userIsPartiallyRegistered,
            userAtLeastPartiallyRegistered,
        } = this.props;

        if (forceConfirmPassword) {
            this.props.openConfirmPasswordModalFromChecker(this.state.guid);
        } else if (forceSignUp && !userLoggedIn && !userAtLeastPartiallyRegistered) {
            this.props.openSignUpModalFromChecker(this.state.guid);
        } else if (forceSignUp && !userLoggedIn && requireLogin) {
            this.determineModalFlow(userIsPartiallyRegistered);
        } else if (!isCompleteProfile && requireCompleteProfile) {
            this.props.openCompleteAccountModalFromChecker(this.state.guid);
        } else if (!userAtLeastPartiallyRegistered && requireUserAtLeastPartiallyRegistered) {
            this.determineModalFlow(userIsPartiallyRegistered);
        } else if (runFunction) {
            runFunction(runFunctionArgument);
        }
    };

    render() {
        return this.props.children
            ? React.cloneElement(React.Children.only(this.props.children), {
                  onClick: this.handleClick,
              })
            : null;
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ShouldLogInContainer);
